import { Logo } from '@bp/ui-components';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AboutBP.module.scss';

type PimHealth = {
  name?: string;
  version?: string;
  status: string;
  ok: boolean;
  neo4j?: {
    name?: string;
    ok: boolean;
    status?: string;
    message?: string;
  };
};

type ApolloHealth = {
  status: string;
  ok: boolean;
  version?: string;
};

export type AboutBPProps = {
  isAdmin: boolean;
  matrixOnline: boolean;
};

export const AboutBP = ({ isAdmin, matrixOnline }: AboutBPProps) => {
  const { t } = useTranslation();

  const [bpGraphHealth, setBpGraphHealth] = useState<ApolloHealth | null>({
    status: 'ERROR',
    ok: false,
  });

  const [pimHealth, setPimHealth] = useState<PimHealth | null>({
    status: 'ERROR',
    ok: false,
  });

  const [deviceId, setDeviceId] = useState<string | null>(null);

  useEffect(() => {
    fetch(import.meta.env.VITE_APP_BACKEND_URI + '/_health')
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }
        throw resp;
      })
      .then((data) => setBpGraphHealth(data))
      .catch(() => {
        setBpGraphHealth({ status: 'ERROR', ok: false });
      });

    fetch(import.meta.env.VITE_APP_AUTH_SERVER_URI + '/_health')
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }
        throw resp;
      })
      .then((data) => setPimHealth(data))
      .catch(() => {
        setPimHealth({ status: 'ERROR', ok: false });
      });

    let isMounted = true;
    CapacitorUpdater.getDeviceId()
      .then((res) => {
        if (isMounted) setDeviceId(res.deviceId);
      })
      .catch(() => {
        // ignore if unavailable (e.g., web)
        setDeviceId('unknown');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  let buildTime: string | undefined = import.meta.env.VITE_APP_BUILD_TIME;
  if (buildTime === undefined) {
    buildTime = 'hmr/live';
  }

  const pimOnline = pimHealth?.status === 'green';
  const graphOnline = bpGraphHealth?.status;

  return (
    <div className={styles['about-bp']}>
      <div className={styles.help}>
        <div className={styles.info}>
          {isAdmin ? (
            <>
              <div className={styles.contact}>
                <div>{t('about.info')}</div>
                <div>+49 (0) 711 – 99 58 80 88</div>
                <div>support@bildungsplattform.org</div>
              </div>
              <div className={styles.documentation}>
                <div>{t('about.documentation')}</div>
                <a target='_blank' href='https://r.tks.eu/bp-doku' rel='noreferrer'>
                  {t('about.documentationLinkText')}
                </a>
              </div>
            </>
          ) : (
            <div>{t('about.askAdmin')}</div>
          )}
        </div>
        <img src={'/digitalpakt_schule_logoleiste.png'} alt={t('about.logosAltText')} />
      </div>
      <div className={styles.footer}>
        <div className={styles.address}>
          <Logo className={styles.logo} type={'tk'} collapsed={false} />
          <div>
            <div>TK-Schulsoftware GmbH & Co. KG</div>
            <div>Rosenwiesstr. 17</div>
            <div>70567 Stuttgart</div>
          </div>
        </div>
        <div className={styles.products}>
          <div className={styles.product}>
            {(APP_VERSION ?? 'dev') + '/' + import.meta.env.VITE_APP_ENVIRONMENT + ' (' + buildTime + ')'}
          </div>
          <div></div>

          <div className={styles.product}>TK-Login (Version: {pimHealth?.version ?? 'unknown'})</div>
          <div className={pimOnline ? styles.online : undefined}>{pimOnline ? 'Online' : 'Offline'}</div>

          <div className={styles.product}>Server (Version: {bpGraphHealth?.version ?? 'unknown'})</div>
          <div className={graphOnline ? styles.online : undefined}>{graphOnline ? 'Online' : 'Offline'}</div>

          <div className={styles.product}>Matrix</div>
          <div className={matrixOnline ? styles.online : undefined}>{matrixOnline ? 'Online' : 'Offline'}</div>

          <div className={styles.product}>Device-ID: {deviceId}</div>
          <div></div>
        </div>
      </div>
    </div>
  );
};
